import React from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Divider, FormControlLabel, Hidden, Radio, RadioGroup, Typography } from '@mui/material';
import GridContainer from '@jumbo/components/GridContainer';
import GridItem from '@jumbo/components/GridItem';
import useImage from 'services/Hooks/useImage';
import SkeletonWrapper from 'Components/SkeletonWrapper';
import CustomInput from '@acnb/CustomComponents/CustomInput/CustomInput';
import { usePatchApplyPromoCode } from 'services/Data/CartService';
import { FileTypeEnumValue } from 'enums/FileTypeEnum';

const useStyles = makeStyles(() => ({
  pageFull: {
    width: '100%',
    marginTop: '1vh',
  },
  noItem: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  center: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  rateOptionLabel: {
    width: '100%',
  },
}));

const formatCarrierRatePrice = rateOption => {
  if (rateOption?.shippingAmount === null || rateOption?.shippingAmount === undefined) {
    return '';
  }

  const shippingAmount = Number(rateOption.shippingAmount);

  if (!Number.isFinite(shippingAmount)) {
    return '';
  }

  return `$${shippingAmount.toFixed(2)} ${rateOption.shippingCurrencyCode || 'CAD'}`;
};

const getCarrierRateMessage = (preview, t) => {
  if (preview?.messageKey) {
    return t(preview.messageKey, {
      skus: preview?.missingWeightSkus?.join(', '),
    });
  }

  return preview?.message || '';
};

const OrderInfoCheckoutSection = ({
  cart,
  isLoading,
  isDisabled,
  fromProfile,
  hasDepletedInventoriedItems,
  carrierRatePreview,
  carrierRatePreviewError,
  isLoadingCarrierRatePreview,
  shouldShowPodShippingRates,
  selectedFreightPodServiceCode,
  selectedCarrierRateOption,
  onSelectedFreightPodServiceCodeChange,
  actionBtns,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const locale = t('locale');

  const getCarrierRateLabel = rateOption => {
    const serviceName = rateOption?.serviceName?.trim();
    const serviceCode = rateOption?.serviceCode?.trim();

    if (rateOption?.serviceNameKey && !serviceName) {
      return t(rateOption.serviceNameKey);
    }

    return serviceName || serviceCode || '';
  };

  const { getInventoryItemImage, getInventoryItemVariantImage, getPanelTypeImage, getToolingImage } = useImage();

  const { mutate: applyPromoCode } = usePatchApplyPromoCode();

  const totalSavings = cart?.cartItems?.reduce((acc, item) => {
    const fullPriceTotal = item.unitPrice * item.quantity;
    const actualTotal = item.subTotal ?? fullPriceTotal;
    const savings = fullPriceTotal - actualTotal;

    return acc + (savings > 0 ? savings : 0);
  }, 0);

  const currentFreightPodCost = Number(cart?.freightPODCost ?? 0);
  const selectedFreightPodCost = selectedCarrierRateOption?.shippingAmount;
  const displayedFreightPodCost = Number(selectedFreightPodCost ?? currentFreightPodCost ?? 0);
  const displayedShippingTotal = Number(cart?.shippingCost || 0) + displayedFreightPodCost;
  const displayedTotal = Number(cart?.total || 0) - currentFreightPodCost + displayedFreightPodCost;
  const inventoriedQuantityExceededItems = cart?.cartItems?.filter(item => item?.isInventoriedItemQuantityExceeded) ?? [];

  return (
    <Box className={classes.pageFull}>
      <GridContainer>
        {hasDepletedInventoriedItems && (
          <GridItem xs={12}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t('inventoriedItemDepletedCheckoutAlert')}
            </Alert>
          </GridItem>
        )}
        {inventoriedQuantityExceededItems.map(item => (
          <GridItem xs={12} key={`inventoried-quantity-exceeded-${item.id}`}>
            <Alert severity="error" sx={{ mb: 2 }}>
              {t('inventoriedItemQuantityExceededWithCounts', {
                availableQuantity: item?.inventoriedQuantityAvailable ?? 0,
                cartQuantity: item?.quantity ?? 0,
              })}
            </Alert>
          </GridItem>
        ))}
        {/* Section for the Cart wall */}
        {cart &&
          cart?.cartWalls?.map(wall => {
            const numberOfPanelImages = wall?.images?.filter(
              i => i.fileType === FileTypeEnumValue.Photo && (!i.numberOfPanels || i.numberOfPanels === wall.numberOfPanels),
            );
            return (
              <React.Fragment key={wall.id}>
                <GridItem xs={12} md={2} key={wall.id}>
                  <Box className={classes.center}>
                    <img
                      src={
                        numberOfPanelImages?.length > 0
                          ? getPanelTypeImage(wall.panelType_Id, numberOfPanelImages[0].id)
                          : '/images/brp-logo.png'
                      }
                      alt="img"
                      style={{ maxWidth: '10vh', maxHeight: '10vh' }}
                    />
                  </Box>
                </GridItem>
                <GridItem xs={12} md={6}>
                  <Typography variant="h2">{wall.name}</Typography>
                  <Typography variant="h3" color="text.secondary" sx={{ mt: 1, mb: 1 }}>
                    {`${wall.numberOfPanels}`} {t('panels')}
                  </Typography>
                  <Box sx={{ mt: 4 }}>
                    {wall?.cartWallInventoryItems?.map(cw => (
                      <Typography
                        key={`${cw.quantity}X ${cw.name}`}
                        variant="h6"
                        color="text.secondary"
                        sx={{ mt: 1, mb: 1 }}>
                        {`${cw.quantity}X ${cw.name}`}
                      </Typography>
                    ))}

                    <Hidden mdDown>
                      <Typography variant="h4" color="text.primary" sx={{ mt: 4 }}>
                        {`${t('quantity')}: ${wall.quantity}`}
                      </Typography>
                    </Hidden>

                    <Hidden mdUp>
                      <Divider />
                      <Box
                        xs={12}
                        md={3}
                        sx={{
                          alignSelf: 'center',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          flexDirection: 'row',
                          width: '100%',
                          my: 3,
                        }}>
                        <Box fontSize={20} display="flex" justifyContent={'center'} alignItems={'center'}>
                          <Box>
                            <Typography variant="h3">{t('quantity')}</Typography>
                          </Box>
                        </Box>
                        <Box display="flex" justifyContent={'center'} alignItems={'center'}>
                          <Typography variant="h3">{wall.quantity}</Typography>
                        </Box>
                      </Box>

                      <Divider />

                      <Box
                        xs={12}
                        md={3}
                        sx={{
                          alignSelf: 'center',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          flexDirection: 'row',
                          width: '100%',
                          my: 3,
                        }}>
                        <Box fontSize={20} display="flex" justifyContent={'center'} alignItems={'center'}>
                          <Box>
                            <Typography variant="h3">{t('price')}</Typography>
                          </Box>
                        </Box>
                        <Box display="flex" justifyContent={'center'} alignItems={'center'}>
                          <Typography variant="h3">
                            ${wall?.subTotal.toFixed(2)} {wall.currencyName}
                          </Typography>
                        </Box>
                      </Box>
                    </Hidden>
                  </Box>
                </GridItem>
                <GridItem xs={12} md={4}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%',
                      width: '100%',
                    }}>
                    <Hidden mdDown>
                      <Typography variant="h1">
                        ${wall?.subTotal.toFixed(2)} {wall.currencyName}
                      </Typography>
                    </Hidden>
                  </Box>
                </GridItem>
                <GridItem xs={12}>
                  <Divider />
                </GridItem>
              </React.Fragment>
            );
          })}

        {/* Section for the Cart tooling */}
        {cart &&
          cart?.cartToolings?.map(tooling => (
            <React.Fragment key={tooling.id}>
              <GridItem xs={12} md={2} key={tooling.id}>
                <Box className={classes.center}>
                  <img
                    src={tooling.imgSrc ? getToolingImage(tooling?.tooling_Id, tooling?.imgSrc.id) : '/images/brp-logo.png'}
                    alt="img"
                    style={{ maxWidth: '10vh', maxHeight: '10vh' }}
                  />
                </Box>
              </GridItem>
              <GridItem xs={12} md={6}>
                <Typography variant="h2">{`${tooling.name}`}</Typography>
                {tooling.additionalToolings?.length > 0 &&
                  tooling.additionalToolings.map(at => (
                    <Typography fontSize={15} color="text.secondary" sx={{ mt: 1, mb: 1 }} key={at.id}>{`${
                      at.quantity
                    } X ${at.name || ''}`}</Typography>
                  ))}

                <Hidden mdDown>
                  <Typography variant="h4" color="text.primary" sx={{ mt: 4 }}>
                    {`${t('quantity')}: ${tooling.quantity}`}
                  </Typography>
                </Hidden>

                <Hidden mdUp>
                  <Divider />
                  <Box
                    xs={12}
                    md={3}
                    sx={{
                      alignSelf: 'center',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexDirection: 'row',
                      width: '100%',
                      my: 3,
                    }}>
                    <Box fontSize={20} display="flex" justifyContent={'center'} alignItems={'center'}>
                      <Box>
                        <Typography variant="h3">{t('quantity')}</Typography>
                      </Box>
                    </Box>
                    <Box display="flex" justifyContent={'center'} alignItems={'center'}>
                      <Typography variant="h3">{tooling.quantity}</Typography>
                    </Box>
                  </Box>

                  <Divider />

                  <Box
                    xs={12}
                    md={3}
                    sx={{
                      alignSelf: 'center',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexDirection: 'row',
                      width: '100%',
                      my: 3,
                    }}>
                    <Box fontSize={20} display="flex" justifyContent={'center'} alignItems={'center'}>
                      <Box>
                        <Typography variant="h3">{t('price')}</Typography>
                      </Box>
                    </Box>
                    <Box display="flex" justifyContent={'center'} alignItems={'center'}>
                      <Typography variant="h3">
                        ${tooling?.subTotal.toFixed(2)} {cart.currencyName}
                      </Typography>
                    </Box>
                  </Box>
                </Hidden>
              </GridItem>

              <GridItem xs={12} md={4} display="flex" justifyContent={'space-between'} alignItems={'center'}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                    width: '100%',
                  }}>
                  <Hidden mdDown>
                    <Typography variant="h1">
                      ${tooling?.subTotal?.toFixed(2)} {cart.currency}
                    </Typography>
                  </Hidden>
                </Box>
              </GridItem>
            </React.Fragment>
          ))}

        {(cart?.cartToolings?.length > 0 || cart?.cartWalls?.length > 0) && (
          <GridItem xs={12}>
            <Divider />
          </GridItem>
        )}
        {/* Section for the Cart items */}
        {cart &&
          cart?.cartItems?.map(item => {
            const variant = item?.inventoryItemVariant;
            const variantDisplayName =
              (locale === 'fr'
                ? variant?.variantNameFr ?? variant?.variantNameEn ?? variant?.variantName
                : variant?.variantNameEn ?? variant?.variantName ?? variant?.variantNameFr) ??
              t('variant', { defaultValue: 'Variant' });
            const variantSku = variant?.variantSku || t('skuUnavailable', { defaultValue: 'SKU unavailable' });

            return (
              <React.Fragment key={item.id}>
                <GridItem xs={12} md={2} key={item.id}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      height: '100%',
                      alignItems: 'center',
                    }}>
                    {item.inventoryItemVariant && item?.inventoryItemVariant.files?.length > 0 ? (
                      <img
                        src={getInventoryItemVariantImage(
                          item?.inventoryItemVariant_Id,
                          item?.inventoryItemVariant.files[0].id,
                        )}
                        alt="img"
                        style={{ maxWidth: '10vh', maxHeight: '10vh' }}
                      />
                    ) : (
                      <img
                        src={
                          item.imgSrc
                            ? getInventoryItemImage(item?.inventoryItem_Id, item?.imgSrc.id)
                            : '/images/brp-logo.png'
                        }
                        alt="img"
                        style={{ maxWidth: '10vh', maxHeight: '10vh' }}
                      />
                    )}
                  </Box>
                </GridItem>
                <GridItem xs={12} md={6}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
                    <Typography variant="h2">{item?.name}</Typography>
                    <Typography variant="h6" color="text.secondary">
                      {item?.sku}
                    </Typography>
                    {variant && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          {t('selectedVariantLabel', { defaultValue: 'Selected variant' })}
                        </Typography>
                        <Typography variant="h6">{variantDisplayName}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {variantSku}
                        </Typography>
                      </Box>
                    )}
                    <Box>
                      <Hidden mdDown>
                        <Typography variant="h4" color="text.primary" sx={{ mt: 4 }}>
                          {t('quantity')}: {item.quantity}
                        </Typography>
                      </Hidden>

                      <Hidden mdUp>
                        <Divider />
                        <Box
                          xs={12}
                          md={3}
                          sx={{
                            alignSelf: 'center',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            flexDirection: 'row',
                            width: '100%',
                            my: 3,
                          }}>
                          <Box fontSize={20} display="flex" justifyContent={'center'} alignItems={'center'}>
                            <Box>
                              <Typography variant="h3">{t('quantity')}</Typography>
                            </Box>
                          </Box>
                          <Box display="flex" justifyContent={'center'} alignItems={'center'}>
                            <Typography variant="h3">{item.quantity}</Typography>
                          </Box>
                        </Box>

                        <Divider />

                        <Box
                          xs={12}
                          md={3}
                          sx={{
                            alignSelf: 'center',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            flexDirection: 'row',
                            width: '100%',
                            my: 3,
                          }}>
                          <Box fontSize={20} display="flex" justifyContent={'center'} alignItems={'center'}>
                            <Box>
                              <Typography variant="h3">{t('price')}</Typography>
                            </Box>
                          </Box>
                          <Box>
                            {item.unitPrice * item.quantity !== item.subTotal ? (
                              <>
                                <Typography variant="h2" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                                  ${(item.unitPrice * item.quantity).toFixed(2)} {item.currency}
                                </Typography>
                                <Typography variant="h1" color="error" fontWeight="bold">
                                  ${item.subTotal.toFixed(2)} {item.currency}
                                </Typography>
                              </>
                            ) : (
                              <Typography variant="h1">
                                ${item.subTotal.toFixed(2)} {item.currency}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Hidden>
                    </Box>
                  </Box>
                </GridItem>
                <GridItem xs={12} md={4}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%',
                      width: '100%',
                    }}>
                    <Hidden mdDown>
                      {item.unitPrice * item.quantity !== item.subTotal ? (
                        <>
                          <Typography variant="h2" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                            ${(item.unitPrice * item.quantity).toFixed(2)} {item.currency}
                          </Typography>
                          <Typography variant="h1" color="error" fontWeight="bold">
                            ${item.subTotal.toFixed(2)} {item.currency}
                          </Typography>
                        </>
                      ) : (
                        <Typography variant="h1">
                          ${item.subTotal.toFixed(2)} {item.currency}
                        </Typography>
                      )}
                    </Hidden>
                  </Box>
                </GridItem>
                <GridItem xs={12}>
                  <Divider />
                </GridItem>
              </React.Fragment>
            );
          })}
      </GridContainer>

      {!cart && (
        <Box className={classes.noItem}>
          <Typography fontSize={20} color="text.secondary" sx={{ mt: 1, mb: 1 }}>
            {t('NoItemInCart')}
          </Typography>
        </Box>
      )}

      <GridContainer>
        {/* Adjust grid width when rendered in profile to accommodate the left support chat panel */}
        <GridItem xs={12} md={fromProfile ? 2 : 6}></GridItem>
        <SkeletonWrapper
          skeletonProps={{
            width: 210,
            height: 33,
          }}
          isLoading={isLoading}
          // Adjust grid width when rendered in profile to accommodate the left support chat panel
          component={
            <GridItem xs={12} md={fromProfile ? 10 : 6} sx={{ mt: 18 }}>
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}>
                  <Box>
                    <Typography variant="h1">{t('subTotal')}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="h1">${(cart?.subTotal ?? 0).toFixed(2)}</Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}>
                  {totalSavings > 0 && (
                    <>
                      <Box>
                        <Typography color="error" variant="h2">
                          {t('totalSavings')}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography color="error" variant="h2">
                          ${totalSavings.toFixed(2)}
                        </Typography>
                      </Box>
                    </>
                  )}
                </Box>
                <Box sx={{ my: 2 }}>
                  <CustomInput
                    labelText={t('promoCode')}
                    id="city"
                    formControlProps={{
                      fullWidth: true,
                    }}
                    inputProps={{
                      onBlur: ({ target: { value } }) => {
                        if (cart.promoCode !== value) {
                          applyPromoCode({ ...cart, promoCode: value });
                        }
                      },
                      disabled: !cart || isDisabled,
                      defaultValue: cart?.promoCode ?? '',
                    }}
                  />
                  {cart?.promoCode && cart?.promoCodeDiscount > 0 && (
                    <Alert severity="success">{t('valid_promo_code')}</Alert>
                  )}
                  {cart?.promoCode && cart?.promoCodeDiscount === null && (
                    <Alert severity="error">{t('invalid_promo_code')}</Alert>
                  )}
                </Box>
                {cart?.promoCodeDiscount && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}>
                    <Box>
                      <Typography variant="h2">{t('discount')}</Typography>
                    </Box>
                    <Box>
                      <Typography x variant="h2">
                        ${(cart?.promoCodeDiscount ?? 0).toFixed(2)}
                      </Typography>
                    </Box>
                  </Box>
                )}
                {cart?.subTotal - cart?.promoCodeDiscount < 0 && (
                  <Alert severity="warning">
                    {t('checkoutDiscoutnWarning', {
                      discountLeftOver: `$${(cart?.promoCodeDiscount - cart?.subTotal).toFixed(2)}`,
                    })}
                  </Alert>
                )}

                {shouldShowPodShippingRates && (
                  <Box sx={{ mt: 3 }}>
                    {isLoadingCarrierRatePreview && <Alert severity="info">{t('checkoutCarrierRates.loading')}</Alert>}

                    {!isLoadingCarrierRatePreview && carrierRatePreviewError && (
                      <Alert severity="error">{t(carrierRatePreviewError)}</Alert>
                    )}

                    {!isLoadingCarrierRatePreview &&
                      !carrierRatePreviewError &&
                      (carrierRatePreview?.messageKey || carrierRatePreview?.message) && (
                        <Alert severity={carrierRatePreview?.shouldRequestRates ? 'warning' : 'info'} sx={{ mb: 2 }}>
                          {getCarrierRateMessage(carrierRatePreview, t)}
                        </Alert>
                      )}

                    {!isLoadingCarrierRatePreview &&
                      !carrierRatePreviewError &&
                      carrierRatePreview?.isEnabled !== false &&
                      carrierRatePreview?.rateOptions?.length > 0 && (
                        <Box>
                          <Alert
                            severity="info"
                            sx={{
                              mb: 2,
                              borderRadius: 2,
                              '& .MuiAlert-message': {
                                width: '100%',
                              },
                            }}>
                            <Typography variant="h4" sx={{ mb: 0.5 }}>
                              {t('checkoutCarrierRates.estimateTitle')}
                            </Typography>
                            <Typography variant="body2">{t('checkoutCarrierRates.estimateDescription')}</Typography>
                          </Alert>

                          <RadioGroup
                            value={selectedFreightPodServiceCode}
                            onChange={event => onSelectedFreightPodServiceCodeChange(event.target.value)}>
                            {carrierRatePreview.rateOptions.map(rateOption => {
                              const isSelected = selectedFreightPodServiceCode === rateOption.serviceCode;

                              return (
                                <FormControlLabel
                                  key={rateOption.serviceCode}
                                  value={rateOption.serviceCode}
                                  disabled={isDisabled}
                                  control={<Radio color="secondary" sx={{ mt: 0.25 }} />}
                                  label={
                                    <Box className={classes.rateOptionLabel}>
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'flex-start',
                                          gap: 2,
                                        }}>
                                        <Box>
                                          <Typography variant="h4" sx={{ fontWeight: 700 }}>
                                            {getCarrierRateLabel(rateOption)}
                                          </Typography>
                                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                            {t('checkoutCarrierRates.estimateBadge')}
                                          </Typography>
                                        </Box>

                                        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                                          <Typography variant="h4" sx={{ fontWeight: 700 }}>
                                            {formatCarrierRatePrice(rateOption)}
                                          </Typography>
                                          {isSelected && (
                                            <Box
                                              sx={{
                                                display: 'inline-flex',
                                                mt: 1,
                                                px: 1.25,
                                                py: 0.35,
                                                borderRadius: 10,
                                                backgroundColor: 'secondary.main',
                                                color: 'secondary.contrastText',
                                              }}>
                                              <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.3 }}>
                                                {t('checkoutCarrierRates.selectedBadge')}
                                              </Typography>
                                            </Box>
                                          )}
                                        </Box>
                                      </Box>
                                    </Box>
                                  }
                                  sx={{
                                    alignItems: 'flex-start',
                                    width: '100%',
                                    m: 0,
                                    mb: 1.5,
                                    px: 1.5,
                                    py: 1.25,
                                    border: '1px solid',
                                    borderColor: isSelected ? 'secondary.main' : 'info.light',
                                    borderRadius: 2,
                                    backgroundColor: isSelected ? 'rgba(255, 193, 7, 0.12)' : 'rgba(2, 136, 209, 0.06)',
                                    boxShadow: isSelected ? '0 8px 24px rgba(0, 0, 0, 0.08)' : 'none',
                                    transition: 'background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
                                    '& .MuiFormControlLabel-label': {
                                      width: '100%',
                                    },
                                    '&:hover': {
                                      backgroundColor: isSelected ? 'rgba(255, 193, 7, 0.16)' : 'rgba(2, 136, 209, 0.1)',
                                    },
                                  }}
                                />
                              );
                            })}
                          </RadioGroup>
                        </Box>
                      )}
                  </Box>
                )}
                {!shouldShowPodShippingRates && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}>
                    <Box>
                      <Typography variant="h2">{t('shipping')}</Typography>
                    </Box>
                    <Box>
                      <Typography x variant="h2">
                        {displayedShippingTotal ? `$${displayedShippingTotal.toFixed(2)}` : t('toBeConfirmed')}
                      </Typography>
                    </Box>
                  </Box>
                )}

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mt: 4,
                  }}>
                  <Box>
                    <Typography variant="h1">{t('total')}</Typography>
                  </Box>
                  <Box>
                    <Typography x variant="h1">
                      ${displayedTotal.toFixed(2)}
                    </Typography>
                  </Box>
                </Box>

                {actionBtns
                  ?.filter(actionBtn => !actionBtn.hidden)
                  .map(actionBtn => (
                    <Button
                      key={actionBtn.label}
                      sx={{
                        borderRadius: 0,
                        color: actionBtn.color === 'error' ? 'white' : '#000',
                        mt: 2,
                        width: '100%',
                        boxShadow: 'none',
                        '&:hover': {
                          boxShadow: 'none',
                        },
                      }}
                      variant={actionBtn.variant}
                      color={actionBtn.color}
                      disabled={!cart || actionBtn.disabled || cart.isDemo}
                      onClick={actionBtn.action}>
                      <Typography variant="h1" sx={{ fontSize: { xs: 16, sm: 18, md: 22 }, py: 2 }}>
                        {actionBtn.label}
                      </Typography>
                    </Button>
                  ))}
              </Box>
            </GridItem>
          }
        />
      </GridContainer>
    </Box>
  );
};

export default OrderInfoCheckoutSection;
